import Box from '@cloudscape-design/components/box';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Spinner from '@cloudscape-design/components/spinner';
import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { NAV_GROUPS } from './nav.config.js';

export interface SectionRegistration {
  id: string;
  /** Lazy import of a React component to mount under the section path (`/{id}/*`). */
  load: () => Promise<{ default: () => ReactNode }>;
}

export interface ConsoleRouterProps {
  sections?: ReadonlyArray<SectionRegistration>;
}

// Overview lives in the shell — a lightweight welcome landing rather than
// a "coming soon" placeholder. When VEXCON-02 ships a real dashboard,
// swap this render for a lazy import of the dashboard package.
const Overview = lazy(async () => ({
  default: () => (
    <ContentLayout
      header={<Header variant="h1" description="Manage identity, edge, compute, and workflows across your Vextura tenants.">Vextura Console</Header>}
    >
      <Box padding="m" />
    </ContentLayout>
  ),
}));

/**
 * Mounts every nav.config section under `/{id}/*` via the section registry.
 *
 * If a section entry appears in the nav but no matching registration was
 * passed in `sections`, the shell navigates back to `/` rather than
 * rendering a placeholder — the core console only lists sections whose
 * `@vextura/service-*` package is bundled, so an unmatched id is a config
 * bug, not a "coming soon" state.
 *
 * The root index route (`/`) is the Overview landing.
 */
export function ConsoleRouter({ sections = [] }: ConsoleRouterProps) {
  const registered = new Map(sections.map((s) => [s.id, s]));

  const sectionRoutes = NAV_GROUPS.flatMap((g) => g.items)
    .filter((item) => item.href !== '/')
    .map((item) => {
      const id = item.id;
      const reg = registered.get(id);
      const Section = reg
        ? lazy(reg.load)
        : lazy(async () => ({
            default: () => <Navigate to="/" replace />,
          }));
      return <Route key={id} path={`${item.href}/*`} element={<Section />} />;
    });

  return (
    <Suspense fallback={<SectionLoading />}>
      <Routes>
        <Route index element={<Overview />} />
        {sectionRoutes}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function SectionLoading() {
  return (
    <Box padding="xxl" textAlign="center">
      <Spinner size="large" />
    </Box>
  );
}
